import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Upload, CheckCircle2 } from "lucide-react";

const PKR = (n) => `Rs ${Number(n || 0).toLocaleString("en-PK")}`;

export default function Checkout() {
  const { items, subtotal, clear, keyOf } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({
    customer_name: "", customer_phone: "", customer_email: "",
    address: "", city: "Besham", notes: "",
    payment_method: "cod", payment_reference: "",
  });
  const [receipt, setReceipt] = useState(null); // data url
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data)); }, []);
  useEffect(() => {
    if (user) setForm((f) => ({ ...f, customer_name: user.name || f.customer_name, customer_email: user.email || f.customer_email }));
  }, [user]);

  const payCfg = settings?.payment_methods || {};
  const deliveryFee = settings?.delivery_fee || 0;
  const total = subtotal + deliveryFee;

  const enabledMethods = Object.entries(payCfg).filter(([_, v]) => v.enabled !== false);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("kind", "receipt");
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setReceipt(data.url);
    } catch (e) {
      setErr(e.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (items.length === 0) { setErr("Cart is empty"); return; }
    if (form.payment_method !== "cod" && !receipt) {
      setErr("Please upload your payment receipt screenshot."); return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        items: items.map((i) => ({ item_id: i.item_id, name: i.name, variant: i.variant, quantity: i.quantity, unit_price: i.unit_price })),
        payment_receipt_b64: receipt,
      };
      const { data } = await api.post("/orders", payload);
      clear();
      nav(`/order-success/${data.order_number}`);
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to place order");
    } finally { setSubmitting(false); }
  };

  if (items.length === 0) {
    return (
      <div className="pt-28 pb-16 max-w-3xl mx-auto px-4 text-center">
        <h1 className="font-serif text-3xl text-white">Your cart is empty</h1>
        <button onClick={() => nav("/menu")} className="btn-gold mt-6">Browse Menu</button>
      </div>
    );
  }

  const method = payCfg[form.payment_method] || {};

  return (
    <div className="pt-24 pb-16 max-w-6xl mx-auto px-4 sm:px-6" data-testid="checkout-page">
      <h1 className="font-serif text-4xl text-white mb-6">Checkout</h1>
      <form onSubmit={submit} className="grid lg:grid-cols-[1fr_380px] gap-8">
        <div className="space-y-6">
          <section className="card-lux p-5">
            <h2 className="font-serif text-xl text-amber-200 mb-4">Delivery details</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              <input required data-testid="co-name" placeholder="Full name" className="input-lux" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} />
              <input required data-testid="co-phone" placeholder="Phone number" className="input-lux" value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} />
              <input type="email" data-testid="co-email" placeholder="Email (optional)" className="input-lux sm:col-span-2" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} />
              <input required data-testid="co-address" placeholder="Delivery address" className="input-lux sm:col-span-2" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
              <input data-testid="co-city" placeholder="City" className="input-lux" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
              <textarea data-testid="co-notes" placeholder="Order notes (optional)" rows={2} className="input-lux sm:col-span-2" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            {!user && <div className="mt-3 text-xs text-zinc-400">Checking out as guest. <button type="button" onClick={() => { const r = window.location.origin + "/account"; window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(r)}`; }} className="text-amber-300 hover:underline">Sign in</button> to track your orders.</div>}
          </section>

          <section className="card-lux p-5">
            <h2 className="font-serif text-xl text-amber-200 mb-4">Payment</h2>
            <div className="grid sm:grid-cols-2 gap-2 mb-4">
              {enabledMethods.map(([key, m]) => (
                <label key={key} data-testid={`pay-${key}`} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer ${form.payment_method === key ? "border-amber-500 bg-amber-500/10" : "border-zinc-800 hover:border-zinc-600"}`}>
                  <input type="radio" name="pay" className="accent-amber-500" checked={form.payment_method === key} onChange={() => { setForm({ ...form, payment_method: key }); setReceipt(null); }} />
                  <span className="text-sm text-white">{m.label}</span>
                </label>
              ))}
            </div>
            {method.instructions && <p className="text-sm text-zinc-300 mb-3">{method.instructions}</p>}
            {form.payment_method !== "cod" && (
              <div className="rounded-lg bg-black/40 p-4 border border-zinc-800 text-sm space-y-1">
                {method.account_title && <div><span className="text-zinc-500">Account Title:</span> <span className="text-white">{method.account_title}</span></div>}
                {method.account_number && <div><span className="text-zinc-500">Account #:</span> <span className="font-mono text-amber-200">{method.account_number}</span></div>}
                {method.bank_name && <div><span className="text-zinc-500">Bank:</span> <span className="text-white">{method.bank_name}</span></div>}
                {method.iban && <div><span className="text-zinc-500">IBAN:</span> <span className="font-mono text-amber-200">{method.iban}</span></div>}
                <div className="pt-3">
                  <input data-testid="co-reference" placeholder="Transaction reference / TID (optional)" className="input-lux" value={form.payment_reference} onChange={(e) => setForm({ ...form, payment_reference: e.target.value })} />
                </div>
                <label className="mt-3 block">
                  <div className="text-xs text-zinc-400 mb-1">Upload receipt screenshot (max 1.5MB)</div>
                  <div className="flex items-center gap-3">
                    <label className="btn-outline-gold cursor-pointer !py-2">
                      <Upload size={14} /> <span className="text-sm">{uploading ? "Uploading..." : "Choose file"}</span>
                      <input type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="co-receipt-input" />
                    </label>
                    {receipt && <img src={receipt} alt="receipt" className="w-16 h-16 rounded-md object-cover border border-amber-500/30" />}
                  </div>
                </label>
              </div>
            )}
          </section>
          {err && <div className="text-sm text-red-400" data-testid="checkout-error">{err}</div>}
        </div>

        {/* Summary */}
        <aside className="card-lux p-5 h-fit lg:sticky lg:top-24">
          <h3 className="font-serif text-xl text-amber-200 mb-4">Order Summary</h3>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {items.map((i) => (
              <div key={keyOf(i)} className="flex justify-between text-sm">
                <span className="text-zinc-300 line-clamp-1">{i.quantity}× {i.name}{i.variant ? ` (${i.variant})` : ""}</span>
                <span className="font-mono text-zinc-200">{PKR(i.unit_price * i.quantity)}</span>
              </div>
            ))}
          </div>
          <div className="divider-gold my-4" />
          <div className="flex justify-between text-sm text-zinc-400"><span>Subtotal</span><span className="font-mono">{PKR(subtotal)}</span></div>
          <div className="flex justify-between text-sm text-zinc-400 mt-1"><span>Delivery</span><span className="font-mono">{PKR(deliveryFee)}</span></div>
          <div className="flex justify-between mt-3 text-white text-lg"><span>Total</span><span className="font-mono text-amber-200">{PKR(total)}</span></div>
          <button data-testid="place-order-btn" disabled={submitting} className="btn-gold w-full mt-5">
            {submitting ? "Placing order..." : <><CheckCircle2 size={16} /> Place Order</>}
          </button>
        </aside>
      </form>
    </div>
  );
}
